import { useEffect, useRef } from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import type { AutocompletePlace } from "src/api/v1.6/autocomplete";
import { AddDestinationButton } from "src/components/TripPlanner/AddDestination/AddDestinationButton";
import styled from "styled-components";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import AddDestinationInline from "./AddDestinationInline/AddDestinationInline";
import { OnscreenReorder } from "./ReorderTrip/OnscreenReorder/OnscreenReorder";
import {
  LoadingCard,
  TripPlannerCard,
} from "./TripPlannerCard/TripPlannerCard";
import {
  TripPlanEntryWrapper,
  TripPlannerPlace,
} from "./TripPlannerCard/TripPlannerPlace";
import { useTripPlannerContext } from "./hooks/useTripPlannerContext";

export function TripPlannerScreenLoaded() {
  const {
    tripPlannerDetails,
    tripPlanningState,
    setHoveredPlaceIndex,
    tripDestination,
  } = useTripPlannerContext();

  const { isLoading, setDestination, shortName, longName } = tripDestination;

  const { isMobile } = useScreenMinWidth();
  const isLargerThanMobile = !isMobile;

  const scrollRef = useRef<HTMLDivElement>(null);
  const addDestinationRef = useRef<HTMLDivElement>(null);

  function onSelectDestination(place: AutocompletePlace) {
    if (place.link) {
      window.open(place.link, "_blank");
    } else {
      setDestination(place);
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (scrollRef.current) {
        scrollIntoView(scrollRef.current, {
          behavior: "smooth",
          block: "center",
        });
      }
    }, 0);

    return () => clearTimeout(timeout);
  }, [tripPlanningState.updatedPlace.value]);

  // Scroll up to add destination inline when last place removed
  function handleRemoveOfLast() {
    setTimeout(() => {
      if (addDestinationRef.current) {
        scrollIntoView(addDestinationRef.current, {
          behavior: "smooth",
          block: "center",
        });
      }
    }, 0);
  }

  return (
    <>
      {isLargerThanMobile ? (
        <OnscreenReorder onRemove={handleRemoveOfLast} />
      ) : (
        <>
          {tripPlannerDetails.places.map((p, index) => {
            const key = `${index}_tripCard_${p.canonicalName}`;
            const callbacks = {
              hoverCallback: () => setHoveredPlaceIndex(index),
              unHoverCallback: () => setHoveredPlaceIndex(undefined),
            };
            const isUpdated = tripPlanningState.updatedPlace.value === index;
            return (
              <TripPlannerPlace
                isLast={index === tripPlannerDetails.places.length - 1}
                index={index}
                key={key}
              >
                <TripPlannerCard
                  scrollRef={isUpdated ? scrollRef : undefined}
                  places={tripPlannerDetails.places}
                  index={index}
                  callbacks={callbacks}
                  isUpdated={isUpdated}
                />
              </TripPlannerPlace>
            );
          })}
        </>
      )}
      {isLoading && (
        <LoadingContainer>
          <TripPlanEntryWrapper $hideTimeline={true}>
            <LoadingCard
              name={longName ?? shortName}
              index={tripPlannerDetails.places.length}
            />
          </TripPlanEntryWrapper>
        </LoadingContainer>
      )}
      <TripPlanEntryWrapper $hideTimeline={true}>
        {isLargerThanMobile ? (
          <AddDestinationInline
            addDestinationRef={addDestinationRef}
            onSelectDestination={onSelectDestination}
          />
        ) : (
          <AddDestinationButton onSelectDestination={onSelectDestination} />
        )}
      </TripPlanEntryWrapper>
    </>
  );
}

const LoadingContainer = styled.div`
  padding-left: 50px;
`;
